import { create } from "zustand";

// Backend sets httpOnly cookies - frontend just manages state
const useAuthStore = create((set, get) => ({
  // State
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,

  // Actions
  setUser: (user) => {
    set({ user, isAuthenticated: !!user });
    if (user) {
      // Store minimal user data in sessionStorage (not sensitive)
      sessionStorage.setItem("user", JSON.stringify(user));
    }
  },

  setLoading: (isLoading) => set({ isLoading }),

  setError: (error) => set({ error }),

  clearError: () => set({ error: null }),

  login: (userData) => {
    // Sanitize user data before storing (remove sensitive fields)
    const roleObject =
      typeof userData.role === "object" && userData.role !== null
        ? userData.role
        : null;
    const sanitizedUser = {
      id: userData.id,
      email: userData.email,
      name: userData.fullName || userData.name, // Backend sends fullName
      role: roleObject ? roleObject.name : userData.role,
      roleId: roleObject?.id ?? userData.roleId ?? undefined,
      roleLevel:
        roleObject?.level ?? userData.roleLevel ?? userData.roleLevelValue,
      // Don't store password or other sensitive data
    };

    set({
      user: sanitizedUser,
      isAuthenticated: true,
      error: null,
    });

    // Store user info in sessionStorage (token is in httpOnly cookie set by backend)
    sessionStorage.setItem("user", JSON.stringify(sanitizedUser));
    sessionStorage.setItem("isAuthenticated", "true");
  },

  logout: () => {
    set({
      user: null,
      isAuthenticated: false,
      error: null,
    });

    // Clear sessionStorage (backend will clear httpOnly cookie)
    sessionStorage.clear();
  },

  updateUser: (userData) => {
    // Sanitize user data before storing (remove sensitive fields)
    const roleObject =
      typeof userData.role === "object" && userData.role !== null
        ? userData.role
        : null;
    const sanitizedUser = {
      id: userData.id,
      email: userData.email,
      name: userData.fullName || userData.name, // Backend sends fullName
      role: roleObject ? roleObject.name : userData.role,
      roleId: roleObject?.id ?? userData.roleId ?? userData.user?.role?.id,
      roleLevel:
        roleObject?.level ??
        userData.roleLevel ??
        userData.user?.role?.level ??
        userData.roleLevelValue,
    };
    set({ user: sanitizedUser });
    sessionStorage.setItem("user", JSON.stringify(sanitizedUser));
  },

  // Initialize auth from sessionStorage when a page needs it
  initializeAuth: async () => {
    const storedUser = sessionStorage.getItem("user");
    const isAuth = sessionStorage.getItem("isAuthenticated");

    if (storedUser && isAuth === "true") {
      try {
        const user = JSON.parse(storedUser);
        set({
          user,
          isAuthenticated: true,
        });

        return;
      } catch (error) {
        console.error("Failed to parse stored user data:", error);
        get().logout();
      }
      return;
    }

    // No local session available.
    get().logout();
  },
}));

export default useAuthStore;
